import type { MarketingTranslator } from '@/i18n/marketing'
import { useTranslation } from 'react-i18next'
interface BriefingChannel {
  iconUrl: string
  title: string
  rows: { label: string; value: string }[]
}

interface SalesBriefingSectionProps {
  /** Section title — "깨봉 러닝센터" */
  titleLine1?: string
  /** "사업설명회 [선착순 모집!]" — pill-highlighted suffix */
  titleLine2Plain?: string
  titleLine2Highlight?: string
  intro?: string
  online?: BriefingChannel
  offline?: BriefingChannel
  note?: string
  onBriefingClick?: () => void
  briefingLabel?: string
  onInquiryClick?: () => void
  inquiryLabel?: string
}

const ICON = '/marketing/franchise'

function getDefaults(mt: MarketingTranslator) {
  const DEFAULTS = {
    titleLine1: mt('header.brand'),
    titleLine2Plain: mt('SalesBriefingSection.DEFAULTS.titleLine2Plain_397'),
    titleLine2Highlight: mt('SalesBriefingSection.DEFAULTS.titleLine2Highlight_398'),
    intro:
      mt('SalesBriefingSection.DEFAULTS.intro_399'),
    briefingLabel: mt('header.briefing'),
    inquiryLabel: mt('aboutLc.aboutLcContent.hero.ctaLabel'),
    note: mt('SalesBriefingSection.DEFAULTS.note_400'),
    online: {
      iconUrl: `${ICON}/icon-tutoring.svg`,
      title: mt('SalesBriefingSection.DEFAULTS.online.title_401'),
      rows: [
        { label: mt('SalesBriefingSection.DEFAULTS.online.rows.0.label_402'), value: mt('SalesBriefingSection.DEFAULTS.online.rows.0.value_403') },
        { label: mt('SalesBriefingSection.DEFAULTS.online.rows.1.label_404'), value: '10:30 ~ 12:00' },
        { label: mt('SalesBriefingSection.DEFAULTS.online.rows.2.label_405'), value: mt('SalesBriefingSection.DEFAULTS.online.rows.2.value_406') },
      ],
    } satisfies BriefingChannel,
    offline: {
      iconUrl: `${ICON}/icon-campus.svg`,
      title: mt('SalesBriefingSection.DEFAULTS.offline.title_407'),
      rows: [
        { label: mt('SalesBriefingSection.DEFAULTS.online.rows.0.label_402'), value: mt('SalesBriefingSection.DEFAULTS.offline.rows.0.value_408') },
        { label: mt('SalesBriefingSection.DEFAULTS.online.rows.1.label_404'), value: '10:30 ~ 12:00' },
        {
          label: mt('SalesBriefingSection.DEFAULTS.offline.rows.2.label_409'),
          value: mt('SalesBriefingSection.DEFAULTS.offline.rows.2.value_410'),
        },
      ],
    } satisfies BriefingChannel,
  }
  return DEFAULTS
}

function ChannelCard({ channel }: { channel: BriefingChannel }) {
  return (
    <div className="marketing-briefing-channel flex-1 flex flex-col gap-4 lg:gap-6 bg-qb-bg-tertiary rounded-[24px] px-6 lg:px-8 py-6 lg:py-8">
      {/* Header — mobile centers icon+title, desktop left-aligns */}
      <div className="flex flex-col gap-3 items-center lg:items-start lg:gap-4">
        <img
          src={channel.iconUrl}
          alt=""
          aria-hidden
          className="w-12 h-12"
        />
        <h3 className="font-bold text-qb-text-high text-[20px] lg:text-[24px] leading-[1.4] tracking-[-0.6px]">
          {channel.title}
        </h3>
      </div>
      <dl className="flex flex-col gap-4 text-[18px] lg:text-[20px] tracking-[-0.6px] leading-[1.5]">
        {channel.rows.map((row) => (
          <div key={row.label} className="flex gap-4 items-start">
            <dt className="font-medium w-18 shrink-0 text-qb-primary">
              {row.label}
            </dt>
            <dd className="font-normal whitespace-pre-line text-qb-text-mid">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

export function SalesBriefingSection(props: SalesBriefingSectionProps) {
  const { t: mt } = useTranslation('marketing')
  const DEFAULTS = getDefaults(mt)
  const {
    titleLine1 = DEFAULTS.titleLine1,
    titleLine2Plain = DEFAULTS.titleLine2Plain,
    titleLine2Highlight = DEFAULTS.titleLine2Highlight,
    intro = DEFAULTS.intro,
    online = DEFAULTS.online,
    offline = DEFAULTS.offline,
    note = DEFAULTS.note,
    onBriefingClick,
    briefingLabel = DEFAULTS.briefingLabel,
    onInquiryClick,
    inquiryLabel = DEFAULTS.inquiryLabel,
  } = props

  const introLines = intro.split('\n')
  const lastIdx = introLines.length - 1

  return (
    <section data-marketing-section="" className="w-full bg-qb-primary pt-14 pb-24 lg:py-24 tracking-[-0.6px]">
      <div className="mx-auto max-w-[1136px] px-6 lg:px-0 flex flex-col items-center gap-10 lg:gap-20">
        {/* Title */}
        <div className="flex flex-col gap-2.5 items-center text-center text-white font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2]">
          <p>{titleLine1}</p>
          <p>
            <span>{titleLine2Plain}</span>
            <span className="text-[#fe0]">{titleLine2Highlight}</span>
          </p>
        </div>

        {/* Card */}
        <div className="w-full max-w-[360px] lg:max-w-[944px] bg-white rounded-[32px] shadow-[0px_4px_8px_rgba(0,0,0,0.15)] p-6 lg:p-10 flex flex-col gap-8 lg:gap-10 items-center">
          {/* Intro — last line painted in qb-primary */}
          <p className="text-center font-bold text-[22px] lg:text-[28px] leading-[1.4] tracking-[-0.6px] text-qb-text-high whitespace-pre-line">
            {introLines.map((line, i) => (
              <span
                key={i}
                className={i === lastIdx ? 'block text-qb-primary' : 'block'}
              >
                {line}
              </span>
            ))}
          </p>

          {/* Channels — desktop: online LEFT / offline RIGHT.
              Mobile reverses to offline TOP / online BOTTOM. */}
          <div className="flex flex-col gap-4 w-full lg:gap-6">
            <div className="flex flex-col-reverse gap-4 w-full lg:flex-row lg:gap-6">
              <ChannelCard channel={online} />
              <ChannelCard channel={offline} />
            </div>
            {note && (
              <p className="font-medium text-qb-text-low text-[16px] lg:text-[18px] leading-[1.5] text-center lg:text-left lg:pl-4 whitespace-pre-line">
                {note}
              </p>
            )}
          </div>

          {/* CTA pair — stacked on mobile, side-by-side on desktop */}
          <div className="marketing-cta-group flex flex-col gap-3 justify-center items-center lg:flex-row lg:gap-4">
            <button
              type="button"
              onClick={onBriefingClick}
              className="marketing-cta w-[160px] lg:w-[211px] inline-flex items-center justify-center h-12 lg:h-14 px-2 lg:px-7 rounded-full bg-qb-primary text-white font-semibold text-[18px] lg:text-[20px] leading-[1.5] tracking-[-0.6px] shadow-[0px_4px_4px_rgba(0,0,0,0.1)] hover:opacity-90 transition-opacity"
            >
              {briefingLabel}
            </button>
            {onInquiryClick && (
              <button
                type="button"
                onClick={onInquiryClick}
                className="marketing-cta w-[160px] lg:w-[211px] inline-flex items-center justify-center h-12 lg:h-14 px-2 lg:px-7 rounded-full bg-[#16224f] text-white font-semibold text-[18px] lg:text-[20px] leading-[1.5] tracking-[-0.6px] shadow-[0px_4px_4px_rgba(0,0,0,0.1)] hover:opacity-90 transition-opacity"
              >
                {inquiryLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
